/**
 * GA4 event tracking helpers.
 *
 * GA4 is initialized once in src/pages/+Head.tsx (SSR entry point) with
 * send_page_view: false; page views are sent, guarded and sanitized, from
 * src/pages/+Layout.tsx. This file only exports convenience wrappers for
 * custom events.
 */

import { sendGaEvent } from '../utils/gaGuard';

/**
 * Track a custom GA4 event
 *
 * GA4-01: delegates to the shared guard in utils/gaGuard.ts, which drops the
 * hit entirely on blocked routes (/auth/callback, /reset-password, /admin/*)
 * and stamps a sanitized `page_location` on the ones it lets through. Every
 * wrapper below, and every caller of `trackEvent`, inherits both.
 *
 * @param eventName - GA4 event name (e.g., 'view_item', 'generate_lead')
 * @param params - Event parameters
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean | undefined>
): void {
  sendGaEvent(eventName, params);
}

// ============================================
// Pre-defined event tracking functions
// ============================================

/**
 * Track when a user views a property
 */
export function trackViewItem(property: {
  id: string;
  title: string;
  price: number;
  currency?: string;
  type?: string;
  city?: string;
}): void {
  trackEvent('view_item', {
    item_id: property.id,
    item_name: property.title,
    price: property.price,
    currency: property.currency || 'KES',
    item_category: property.type,
    item_variant: property.city,
  });
}

/**
 * Track when a user contacts an agent (lead generation)
 */
export function trackGenerateLead(property: {
  id: string;
  title: string;
  agentId?: string;
}): void {
  trackEvent('generate_lead', {
    item_id: property.id,
    item_name: property.title,
    lead_source: 'contact_agent',
  });
}

/**
 * Track when a user performs a search
 */
export function trackSearch(searchTerm: string, resultsCount?: number): void {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
  });
}

/**
 * Track when a user saves a property (add to wishlist)
 */
export function trackAddToWishlist(property: {
  id: string;
  title: string;
  price: number;
}): void {
  trackEvent('add_to_wishlist', {
    item_id: property.id,
    item_name: property.title,
    price: property.price,
    currency: 'KES',
  });
}

// GA4-05: `trackSignUp` / `trackLogin` used to live here. The authentication
// events now have exactly one home, utils/gaAuthEvents.ts, and the event names
// are pinned there repo-wide — see that file for the taxonomy.
